/**
 * FileUploadZone Component
 * Drag-and-drop file upload component with validation
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';

export interface FileUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  maxFiles?: number;
  className?: string;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  error?: string;
}

export function FileUploadZone({
  onFilesSelected,
  acceptedFileTypes = ['image/*', 'application/pdf'],
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 5,
  className,
  disabled = false,
  label = 'Upload Files',
  helperText = 'Drag and drop images or PDFs here, or click to select files. Only images and PDFs are allowed.',
  error,
}: FileUploadZoneProps) {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const previews = useMemo(() => {
    return uploadedFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      isImage: file.type.startsWith('image/'),
      isPdf: file.type === 'application/pdf',
    }));
  }, [uploadedFiles]);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      console.warn('Some files were rejected:', rejectedFiles);
    }
    
    const newFiles = [...uploadedFiles, ...acceptedFiles].slice(0, maxFiles);
    setUploadedFiles(newFiles);
    onFilesSelected(newFiles);
  }, [uploadedFiles, maxFiles, onFilesSelected]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    onDrop,
    accept: acceptedFileTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {} as Record<string, string[]>),
    maxSize: maxFileSize,
    maxFiles,
    disabled,
  });

  const removeFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    onFilesSelected(newFiles);
  };

  const closePreview = () => {
    setPreviewIndex(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const hasError = error || fileRejections.length > 0;

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      <div
        {...getRootProps()}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
          isDragActive && !isDragReject && 'border-primary-400 bg-primary-50',
          isDragReject && 'border-error-400 bg-error-50',
          hasError && 'border-error-400',
          !isDragActive && !hasError && 'border-gray-300 hover:border-gray-400',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <input {...getInputProps()} />
        
        <div className="space-y-2">
          <svg
            className={cn(
              'mx-auto h-12 w-12',
              isDragActive && !isDragReject && 'text-primary-400',
              isDragReject && 'text-error-400',
              !isDragActive && !hasError && 'text-gray-400'
            )}
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          
          <div>
            <p className="text-sm text-gray-600">
              {isDragActive
                ? isDragReject
                  ? 'Some files are not supported'
                  : 'Drop files here...'
                : helperText
              }
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: {acceptedFileTypes.join(', ')} • Max size: {formatFileSize(maxFileSize)} • Max files: {maxFiles}
            </p>
          </div>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="mt-2"
          >
            Choose Files
          </Button>
        </div>
      </div>

      {/* File List */}
      {uploadedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Uploaded Files ({uploadedFiles.length})</h4>
          <div className="space-y-2">
            {previews.map((p, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-white border flex items-center justify-center">
                    {p.isImage ? (
                      <img src={p.url} alt={p.file.name} className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(p.file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewIndex(index)}
                  >
                    Preview
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="text-error-600 hover:text-error-700"
                    aria-label={`Remove ${p.file.name}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Messages */}
      {(error || fileRejections.length > 0) && (
        <div className="mt-2">
          {error && (
            <p className="text-sm text-error-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
          {fileRejections.map(({ file, errors }, index) => (
            <p key={index} className="text-sm text-error-600 mt-1">
              {file.name}: {errors.map(e => e.message).join(', ')}
            </p>
          ))}
        </div>
      )}

      {/* Simple Preview Modal */}
      {previewIndex !== null && previews[previewIndex] && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full h-[80vh] flex flex-col">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="text-sm font-medium truncate">{previews[previewIndex].file.name}</div>
              <Button variant="ghost" size="sm" onClick={closePreview} aria-label="Close preview">✕</Button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-50 p-2">
              {previews[previewIndex].isImage && (
                <img src={previews[previewIndex].url} alt={previews[previewIndex].file.name} className="max-w-full max-h-full mx-auto" />
              )}
              {previews[previewIndex].isPdf && (
                <object data={previews[previewIndex].url} type="application/pdf" className="w-full h-full">
                  <p className="p-4 text-sm text-gray-700">PDF preview not supported in this browser. <a href={previews[previewIndex].url} target="_blank" rel="noreferrer" className="text-primary-700 underline">Open in new tab</a>.</p>
                </object>
              )}
              {!previews[previewIndex].isImage && !previews[previewIndex].isPdf && (
                <div className="p-4 text-sm text-gray-700">Preview not available for this file type. You can download and view it locally.</div>
              )}
            </div>
            <div className="p-3 border-t flex items-center justify-end">
              <Button variant="outline" onClick={closePreview}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
